import { useEffect, useMemo, useState, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Table, Pagination, Select, Button } from '@anyemp/ui-kit';
import toast from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';
import { ticketService } from '../services/ticketService';
import { StatusBadge, PriorityBadge, PriorityDot, timeAgo } from '../components/Support/ticketBadges';
import TicketCreateModal from '../components/Support/TicketCreateModal';
import TicketDetailModal from '../components/Support/TicketDetailModal';
import { useTicketOptions } from '../components/Support/ticketOptions';
import { useRealtimeEvent } from '../hooks/useRealtimeEvent';
import { PAGINATION_OPTIONS, DEFAULT_PAGE_SIZE } from '../utils/paginationConstants';
import { useTableColumns, loadColumnsStateFromLS } from '../utils/tableColumnsPersistence';
import useResponsiveViewPreference from '../hooks/useResponsiveViewPreference';
import useLatestRequest from '../hooks/useLatestRequest';
import DataViewToggle from '../components/UI/DataViewToggle';
import { RecordCard, RecordCardField, RecordCardFields, RecordCardHeader, ResponsiveCardGrid } from '../components/UI/ResponsiveRecordCards';

const SERVICE = 'talent-service';
const COLUMNS_LS_KEY = 'support_tickets_columns';

const titleize = (v) => (v ? String(v).replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()) : '—');
const asArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);
const authorLabel = (a, id) => (a ? (a.fullName || a.full_name || a.name || a.email || id) : id);

/**
 * Admin "all tickets" view for the service — only for users who can list all
 * tickets (support-service:ticket:list). Everyone else uses the floating widget
 * for their own tickets. Always requests scope=all.
 */
export default function SupportTickets() {
  const { hasPermission } = useAuth();
  const canListAll = hasPermission('support-service', 'ticket', 'list');
  const { status: statusOptions, priority: priorityOptions, category: categoryOptions } = useTicketOptions();

  // All filters are multi-select (arrays). Empty array = no filter (all).
  const [fStatus, setFStatus] = useState([]);
  const [fPriority, setFPriority] = useState([]);
  const [fCategory, setFCategory] = useState([]);
  const [fAuthor, setFAuthor] = useState([]);
  const [authorOptions, setAuthorOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState(null);
  const { mode: viewMode, selectMode: setViewMode } = useResponsiveViewPreference('support-tickets');

  // Only the latest request may write the table (filters/page can change
  // faster than the server answers)
  const startListRequest = useLatestRequest();

  const load = useCallback(async () => {
    const isCurrent = startListRequest();
    setLoading(true);
    try {
      const params = { service: SERVICE, scope: 'all', page, limit };
      if (fStatus.length) params.status = fStatus;
      if (fPriority.length) params.priority = fPriority;
      if (fCategory.length) params.category = fCategory;
      if (fAuthor.length) params.author = fAuthor;
      const body = await ticketService.list(params);
      if (!isCurrent()) return;
      const data = body?.data || [];
      setRows(data);
      setTotal(body?.pagination?.total || 0);
      // Accumulate distinct authors for the author filter (grows as you page).
      setAuthorOptions((prev) => {
        const seen = new Map(prev.map((o) => [o.value, o]));
        data.forEach((t) => {
          const id = t.author_id != null ? String(t.author_id) : null;
          if (id && !seen.has(id)) seen.set(id, { value: id, label: authorLabel(t.author, id) });
        });
        return [...seen.values()];
      });
    } catch (e) {
      if (!isCurrent()) return;
      toast.error(e?.response?.data?.message || 'Failed to load tickets');
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [fStatus, fPriority, fCategory, fAuthor, page, limit, startListRequest]);

  useEffect(() => {
    if (canListAll) load();
  }, [load, canListAll]);

  // Новый тикет мог прийти из любого микросервиса — подтягиваем список сразу,
  // не дожидаясь перезагрузки страницы. Соединение общее, своё не поднимаем
  useRealtimeEvent('support:ticket:created', useCallback(() => {
    if (canListAll) load();
  }, [canListAll, load]));

  const columns = useMemo(() => [
    { key: 'ticket_number', label: '#', width: 110, render: (r) => <span className="font-mono text-xs text-fg-tertiary">{r.ticket_number}</span> },
    { key: 'title', label: 'Title', render: (r) => (
      <span className="inline-flex items-center gap-2 min-w-0">
        <PriorityDot priority={r.priority} />
        <span className="truncate">{r.title}</span>
      </span>
    ) },
    { key: 'status', label: 'Status', width: 130, render: (r) => <StatusBadge status={r.status} /> },
    { key: 'priority', label: 'Priority', width: 110, render: (r) => <PriorityBadge priority={r.priority} /> },
    { key: 'category', label: 'Category', width: 120, render: (r) => titleize(r.category) },
    { key: 'author', label: 'Author', width: 160, render: (r) => (r.author ? (r.author.fullName || r.author.full_name || r.author.name || r.author.email) : (r.author_id || '—')) },
    { key: 'source_label', label: 'Source', width: 160, render: (r) => r.source_label || '—' },
    { key: 'created_at', label: 'Created', width: 120, render: (r) => <span title={new Date(r.created_at).toLocaleString()}>{timeAgo(r.created_at)}</span> },
  ], []);

  // Column management (reorder / hide / pin) persisted to localStorage.
  const tableColumnsState = useTableColumns(columns, {
    ...loadColumnsStateFromLS(COLUMNS_LS_KEY, { defaultPinned: ['ticket_number'], defaultHidden: [] }),
    onChange: (state) => {
      try { localStorage.setItem(COLUMNS_LS_KEY, JSON.stringify(state)); } catch { /* ignore */ }
    },
  });

  // Admin-only page: users without list permission are sent home (they use the widget).
  if (!canListAll) return <Navigate to="/" replace />;

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary">All support tickets</h1>
          <p className="text-sm text-fg-tertiary mt-0.5">{SERVICE} · {total} total</p>
        </div>
        <Button color="primary" onClick={() => setCreating(true)}>
          <Plus size={16} /> New ticket
        </Button>
        <DataViewToggle value={viewMode} onChange={setViewMode} />
      </div>

      <div className="flex items-end gap-3 flex-wrap">
        <div className="w-full min-w-0 sm:w-56">
          <Select label="Status" options={statusOptions} value={fStatus} onChange={(v) => { setFStatus(asArray(v)); setPage(1); }} multiple clearable placeholder="All statuses" fullWidth />
        </div>
        <div className="w-full min-w-0 sm:w-56">
          <Select label="Priority" options={priorityOptions} value={fPriority} onChange={(v) => { setFPriority(asArray(v)); setPage(1); }} multiple clearable placeholder="All priorities" fullWidth />
        </div>
        <div className="w-full min-w-0 sm:w-56">
          <Select label="Category" options={categoryOptions} value={fCategory} onChange={(v) => { setFCategory(asArray(v)); setPage(1); }} multiple clearable placeholder="All categories" fullWidth />
        </div>
        <div className="w-full min-w-0 sm:w-64">
          <Select label="Author" options={authorOptions} value={fAuthor} onChange={(v) => { setFAuthor(asArray(v)); setPage(1); }} multiple clearable placeholder="All authors" fullWidth />
        </div>
      </div>

      {viewMode === 'cards' && (
        <div className="space-y-4">
          {loading ? <div className="py-12 text-center text-fg-tertiary">Loading tickets…</div> : rows.length ? (
            <ResponsiveCardGrid>
              {rows.map((row) => (
                <RecordCard key={row.id} onClick={() => setOpenId(row.id)} actionLabel={`Open ticket ${row.ticket_number || row.id}`}>
                  <RecordCardHeader title={<span className="inline-flex items-center gap-2"><PriorityDot priority={row.priority} />{row.title}</span>} subtitle={row.ticket_number} badge={<StatusBadge status={row.status} />} />
                  <RecordCardFields>
                    <RecordCardField label="Priority"><PriorityBadge priority={row.priority} /></RecordCardField>
                    <RecordCardField label="Category">{titleize(row.category)}</RecordCardField>
                    <RecordCardField label="Author">{authorLabel(row.author, row.author_id)}</RecordCardField>
                    <RecordCardField label="Created">{timeAgo(row.created_at)}</RecordCardField>
                  </RecordCardFields>
                </RecordCard>
              ))}
            </ResponsiveCardGrid>
          ) : <div className="py-12 text-center text-fg-tertiary">No tickets</div>}
          <div className="overflow-x-auto">
            <Pagination currentPage={page} totalPages={totalPages} totalItems={total} itemsPerPage={limit} onPageChange={setPage} perPageOptions={PAGINATION_OPTIONS} perPage={limit} onPerPageChange={(v) => { setLimit(Number(v)); setPage(1); }} showResults="range" showOnSinglePage hasFirstLast hasGoToPage />
          </div>
        </div>
      )}
      {viewMode === 'table' && <Table
        columns={columns}
        data={rows}
        isLoading={loading}
        rowKey={(r) => r.id}
        onRowClick={(r) => setOpenId(r.id)}
        columnMenu
        {...tableColumnsState}
        emptyState={<div className="py-10 text-center text-fg-tertiary">No tickets</div>}
        hasRowDividers
        footer={
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={total}
            itemsPerPage={limit}
            onPageChange={setPage}
            perPageOptions={PAGINATION_OPTIONS}
            perPage={limit}
            onPerPageChange={(v) => { setLimit(Number(v)); setPage(1); }}
            showResults="range"
            showOnSinglePage
            hasFirstLast
            hasGoToPage
          />
        }
      />}

      <TicketCreateModal open={creating} onClose={() => setCreating(false)} onCreated={load} serviceName={SERVICE} sourceLabel={`Tickets · ${SERVICE}`} />
      <TicketDetailModal ticketId={openId} open={!!openId} onClose={() => setOpenId(null)} onChanged={load} />
    </div>
  );
}
